import { ofetch } from 'ofetch'
import { useAlertStore } from '~/stores/alert'
import { useAuthStore } from '~/stores/auth'
import { useSentry } from '~/composables/useSentry'
import { useEvent } from '~/composables/api/useEvent'
import { useSettingsStore } from '../stores/settings'

const defaultRepeat = 1
const maxEventIdWaitAttempts = 1 // Maximum attempts to wait for event ID
const eventIdWaitDelay = 100 // Delay between attempts in ms

const useCustomFetch = async <T = any>(url: string, options: any, requireEventId = true, repeat: number = defaultRepeat, eventIdWaitAttempts: number = maxEventIdWaitAttempts): Promise<T> => {
  const { logout } = useAuth()
  const alertStore = useAlertStore()
  const authStore = useAuthStore()
  const settingsStore = useSettingsStore()
  const { captureException } = useSentry()
  const config = useRuntimeConfig()
  const { eventId } = useContext()
  const { $refreshToken } = useNuxtApp() as any
  
  if (requireEventId && !eventId.value) {
    if (eventIdWaitAttempts > 0) {
      await new Promise(resolve => setTimeout(resolve, eventIdWaitDelay))
      return useCustomFetch<T>(url, options, requireEventId, repeat, eventIdWaitAttempts - 1)
    } else {
      throw new Error(`Event ID is required but not available after ${maxEventIdWaitAttempts} attempts. Please ensure admin settings are loaded.`)
    }
  }

  try {
    const headers = {
      ...options.headers,
      ...(requireEventId && eventId.value && { 'x-event-id': eventId.value })
    }

    const apiFetch = ofetch.create({
      headers,
    })
    
    return await apiFetch<T>(url, {
      ...options,
      headers,
      credentials: options.credentials ?? 'include',
    })
  } catch (error: any) {
    if (error.status === 401 && repeat > 0) {
      // Delegate to the shared refresh in auth-check.client.ts so N concurrent
      // 401s from a stale-token page load collapse onto a single /auth/refresh
      // round trip instead of N parallel ones.
      const refreshed = $refreshToken ? await $refreshToken() : false

      if (refreshed) {
        return await useCustomFetch<T>(url, options, requireEventId, repeat - 1, maxEventIdWaitAttempts)
      }

      if (authStore.user) {
        alertStore.setAlert('Your session has expired. Please log in again.', 'error')
        logout()
      }
      throw error
    }

    throw error
  }
}

export default useCustomFetch
